/**
 * Toast helper (Plan 02-09 / minimal Phase-2 v1).
 *
 * Provides a headless `toast(...)` function + a tiny in-memory store +
 * `subscribe()`, consumed by `features/toast/ToastViewport`. The
 * store keeps last-N entries and exposes test helpers (`__resetToasts`,
 * `__getToasts`) so unit tests can assert what was queued without
 * mounting a viewport.
 *
 * This file is INTENTIONALLY small — Plan 11 will replace with a richer
 * Phase-2 toast system if/when needed (auto-dismiss timing, focus
 * management). For now, the contract `useChoreActions` consumes is:
 *
 *   toast({ kind: 'info' | 'warn' | 'error' | 'success', body: string })
 *
 * No extra fields, no JSX-children, no portal. The 409 / 403 / 500 paths
 * in useChoreActions all funnel through this single helper.
 */

export type ToastKind = 'info' | 'warn' | 'error' | 'success';

export interface ToastEntry {
  id: number;
  kind: ToastKind;
  body: string;
  /** ms since unix epoch; useful for test assertions on ordering. */
  at: number;
  /** Optional stable test handle, rendered as `data-testid` by the viewport. */
  testId?: string;
}

interface ToastInput {
  kind: ToastKind;
  body: string;
  /** Optional stable test handle, carried through onto the entry. */
  testId?: string;
}

/**
 * The queue is REPLACED on every change, never mutated in place, so the array
 * handed to subscribers is a valid immutable snapshot: its identity changes if
 * and only if the queue changed. That is what lets a consumer cache it (and
 * what makes `useSyncExternalStore` safe here).
 */
let _entries: ToastEntry[] = [];
let _next = 1;
const _listeners = new Set<(entries: readonly ToastEntry[]) => void>();

function emit() {
  for (const l of _listeners) l(_entries);
}

/** Public API — fire a toast. Returns the assigned id (no-op semantics). */
export function toast(input: ToastInput): number {
  const id = _next++;
  _entries = [
    ..._entries,
    {
      id,
      kind: input.kind,
      body: input.body,
      at: Date.now(),
      ...(input.testId === undefined ? {} : { testId: input.testId }),
    },
  ];
  // Cap to the last 8 entries so a chain of 409s doesn't unbound-grow.
  if (_entries.length > 8) _entries = _entries.slice(-8);
  emit();
  return id;
}

/**
 * Empty the queue. Idempotent: clearing an already-empty queue is a no-op and
 * deliberately does NOT `emit()`, so a viewport clearing an already-cleared
 * surface cannot loop itself awake.
 */
export function clearToasts(): void {
  if (_entries.length === 0) return;
  _entries = [];
  emit();
}

/** Subscribe a viewport component; returns disposer. */
export function subscribeToasts(
  fn: (entries: readonly ToastEntry[]) => void,
): () => void {
  _listeners.add(fn);
  fn(_entries);
  return () => {
    _listeners.delete(fn);
  };
}

// ─── Test helpers (named with __ prefix to discourage prod use) ────────────

/**
 * Reset the queue between tests. `_next` deliberately survives: ids are never
 * reused, so nothing keyed on an id can confuse a new toast for an old one.
 */
export function __resetToasts(): void {
  clearToasts();
}

/** Read current queue snapshot (test-only). */
export function __getToasts(): readonly ToastEntry[] {
  return [..._entries];
}
